/**
 * Small UI primitives.
 *
 * These are the only components allowed to reference raw class names from
 * `styles.css` outside of a mode component. Everything else composes these.
 */

import { useEffect, useId, useRef, type ReactNode } from "react";
import { IconAlert, IconCheck, IconClose, IconSwap } from "./icons";

// ---------------------------------------------------------------------------
//  Button
// ---------------------------------------------------------------------------

type ButtonVariant = "default" | "primary" | "ghost" | "danger";

interface ButtonProps {
  children: ReactNode;
  onClick?: () => void;
  variant?: ButtonVariant;
  size?: "sm" | "md" | "lg";
  iconOnly?: boolean;
  block?: boolean;
  disabled?: boolean;
  busy?: boolean;
  title?: string;
  type?: "button" | "submit";
  "aria-label"?: string;
}

export function Button({
  children,
  onClick,
  variant = "default",
  size = "md",
  iconOnly = false,
  block = false,
  disabled = false,
  busy = false,
  title,
  type = "button",
  "aria-label": ariaLabel,
}: ButtonProps) {
  const classes = [
    "btn",
    variant !== "default" ? `btn--${variant}` : "",
    size === "lg" ? "btn--lg" : "",
    iconOnly ? "btn--icon" : "",
    block ? "btn--block" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type={type}
      className={classes}
      onClick={onClick}
      disabled={disabled || busy}
      title={title}
      aria-label={ariaLabel}
      aria-busy={busy || undefined}
    >
      {busy ? <span className="spinner" /> : null}
      {children}
    </button>
  );
}

export function IconButton({
  label,
  onClick,
  children,
  disabled = false,
  active = false,
  title,
}: {
  label: string;
  onClick?: () => void;
  children: ReactNode;
  disabled?: boolean;
  active?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      className="btn btn--ghost btn--icon"
      onClick={onClick}
      disabled={disabled}
      title={title ?? label}
      aria-label={label}
      aria-pressed={active}
      style={active ? { color: "var(--accent)", background: "var(--bg-panel-hover)" } : undefined}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
//  Segmented control
// ---------------------------------------------------------------------------

export interface SegmentedOption<T extends string> {
  readonly value: T;
  readonly label: string;
  readonly icon?: ReactNode;
  readonly title?: string;
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  tone = "default",
  label,
  iconsOnMobile = false,
}: {
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  tone?: "default" | "brand";
  label?: string;
  /** On phones the text labels collapse, leaving icon-only tabs. */
  iconsOnMobile?: boolean;
}) {
  return (
    <div className="segmented" role="tablist" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="tab"
          className="segmented__item"
          data-tone={tone}
          aria-selected={option.value === value}
          title={option.title ?? option.label}
          onClick={() => onChange(option.value)}
        >
          {option.icon}
          {iconsOnMobile ? <span className="hide-sm">{option.label}</span> : option.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Toggle / range
// ---------------------------------------------------------------------------

export function Toggle({
  checked,
  onChange,
  label,
  disabled = false,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <label className="toggle" title={label}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="toggle__track">
        <span className="toggle__thumb" />
      </span>
      <span className="sr-only">{label}</span>
    </label>
  );
}

export function Range({
  value,
  min,
  max,
  step,
  onChange,
  label,
}: {
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  label: string;
}) {
  const id = useId();
  const fill = ((value - min) / (max - min)) * 100;
  return (
    <>
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="range"
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        style={{ "--fill": `${fill}%` } as React.CSSProperties}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
//  Language bar
// ---------------------------------------------------------------------------

export function SwapButton({ onClick, disabled = false }: { onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      className="swap"
      onClick={onClick}
      disabled={disabled}
      title="Swap source and target languages"
      aria-label="Swap source and target languages"
    >
      <IconSwap size={17} />
    </button>
  );
}

// ---------------------------------------------------------------------------
//  Status
// ---------------------------------------------------------------------------

export function Status({ state, detail }: { state: string; detail?: string }) {
  return (
    <span className="status" data-state={state} title={detail ?? state}>
      <span className="status__dot" />
      {state}
    </span>
  );
}

export function Badge({ children, tone = "default", title }: { children: ReactNode; tone?: "default" | "accent" | "warn" | "good"; title?: string }) {
  return (
    <span className={`badge${tone === "default" ? "" : ` badge--${tone}`}`} title={title}>
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
//  Toasts
// ---------------------------------------------------------------------------

export type ToastTone = "info" | "success" | "warn" | "error";

export interface Toast {
  readonly id: string;
  readonly title: string;
  readonly tone: ToastTone;
  readonly detail?: string;
}

const TOAST_TTL_MS: Readonly<Record<ToastTone, number>> = {
  info: 4_000,
  success: 3_500,
  warn: 7_000,
  error: 9_000,
};

export function ToastStack({ toasts, onDismiss }: { toasts: readonly Toast[]; onDismiss: (id: string) => void }) {
  return (
    <div className="toasts" role="status" aria-live="polite">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    timer.current = setTimeout(() => onDismiss(toast.id), TOAST_TTL_MS[toast.tone]);
    return () => {
      if (timer.current !== null) clearTimeout(timer.current);
    };
  }, [onDismiss, toast.id, toast.tone]);

  const Icon = toast.tone === "error" || toast.tone === "warn" ? IconAlert : IconCheck;

  return (
    <div className="toast" data-tone={toast.tone}>
      <span
        style={{
          color: toast.tone === "error" ? "var(--danger)" : toast.tone === "warn" ? "var(--warning)" : "var(--success)",
          marginTop: 2,
        }}
      >
        <Icon size={16} />
      </span>
      <div className="toast__body grow">
        <div className="toast__title">{toast.title}</div>
        {toast.detail ? <div className="toast__detail">{toast.detail}</div> : null}
      </div>
      <IconButton label="Dismiss" onClick={() => onDismiss(toast.id)}>
        <IconClose size={14} />
      </IconButton>
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Modal
// ---------------------------------------------------------------------------

export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  wide = false,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    // Move focus into the dialog so keyboard users are not left behind it.
    ref.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, open]);

  if (!open) return null;

  return (
    <div
      className="scrim"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className={`modal${wide ? " modal--wide" : ""}`} role="dialog" aria-modal="true" aria-label={title} ref={ref} tabIndex={-1}>
        <div className="modal__head">
          <h2 className="modal__title">{title}</h2>
          <IconButton label="Close" onClick={onClose}>
            <IconClose size={16} />
          </IconButton>
        </div>
        <div className="modal__body">{children}</div>
        {footer ? <div className="modal__foot">{footer}</div> : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Setting row
// ---------------------------------------------------------------------------

export function Setting({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="setting">
      <div>
        <div className="setting__label">{label}</div>
        {hint ? <div className="setting__hint">{hint}</div> : null}
      </div>
      <div className="setting__control">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Panel
// ---------------------------------------------------------------------------

export function Panel({
  title,
  actions,
  children,
  flush = false,
  className = "",
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  flush?: boolean;
  className?: string;
}) {
  return (
    <section className={`panel${flush ? " panel--flush" : ""} ${className}`}>
      {title ? (
        <header className="panel__head">
          <div className="panel__title">{title}</div>
          {actions ? <div className="row">{actions}</div> : null}
        </header>
      ) : null}
      {children}
    </section>
  );
}

export function Empty({ title, hint, icon }: { title: string; hint?: string; icon?: ReactNode }) {
  return (
    <div className="stream__empty">
      {icon ? <div className="dim">{icon}</div> : null}
      <div style={{ fontWeight: 550 }}>{title}</div>
      {hint ? <div style={{ fontSize: "var(--step--1)", maxWidth: "34ch" }}>{hint}</div> : null}
    </div>
  );
}
